import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import {
  CookieConsent,
  DemoFormModal,
  Footer,
  Header,
  WhatsAppFloatButton,
} from "@/pages/landingPage/components";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL } from "../content/company";
import type { LegalDocument } from "../content/types";
import { useActiveSection } from "../hooks/useActiveSection";
import { markdownPath } from "../toMarkdown";
import { plainText } from "../utils";
import { LegalAudiencePaths } from "./legalAudiencePaths";
import { LegalChangelog } from "./legalChangelog";
import { LegalContactCard } from "./legalContactCard";
import { LegalHero } from "./legalHero";
import { LegalHighlights } from "./legalHighlights";
import { LegalRelatedDocs } from "./legalRelatedDocs";
import { LegalSectionView } from "./legalSectionView";
import { LegalToc } from "./legalToc";

type LegalLayoutProps = {
  document: LegalDocument;
};

/**
 * A casca de todo documento jurídico da Fidexa: mesmo head, mesma ordem de
 * leitura (essencial → para quem → índice e texto → encarregado → histórico →
 * outros documentos). Documento novo é só conteúdo em `content/`.
 */
export const LegalLayout = ({ document }: LegalLayoutProps) => {
  useScrollDepth();
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const sectionIds = useMemo(
    () => document.sections.map(section => section.id),
    [document.sections]
  );
  const activeId = useActiveSection(sectionIds);

  const url = `${SITE_URL}${document.path}`;
  const ogTitle = `${document.seoTitle} | Fidexa`;

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Início",
        item: `${SITE_URL}/`,
      },
      { "@type": "ListItem", position: 2, name: document.title, item: url },
    ],
  };

  const webPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: document.title,
    url,
    description: document.description,
    inLanguage: "pt-BR",
    dateModified: document.updatedAt,
    publisher: { "@type": "Organization", name: "Fidexa", url: SITE_URL },
  };

  // FAQPage só quando a página tem perguntas frequentes. A resposta vai sem a
  // marcação do texto (negrito e link), que o JSON-LD não entende.
  const faqItems = document.sections.flatMap(section =>
    section.blocks.flatMap(block => (block.type === "faq" ? block.items : []))
  );
  const faqPage =
    faqItems.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map(item => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: plainText(item.answer),
            },
          })),
        }
      : null;

  const openDemo = () => {
    trackEvent({ event: "cta_click_specialist", location: "header" });
    setIsDemoOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{document.seoTitle}</title>
        <meta name="description" content={document.description} />
        <link rel="canonical" href={url} />
        {document.noindex ? (
          <meta name="robots" content="noindex, follow" />
        ) : (
          // Versão para LLMs, gerada no build (scripts/legal-markdown.mjs).
          <link
            rel="alternate"
            type="text/markdown"
            href={`${SITE_URL}${markdownPath(document.path)}`}
          />
        )}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={document.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={document.description} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(webPage)}</script>
        {faqPage && (
          <script type="application/ld+json">{JSON.stringify(faqPage)}</script>
        )}
      </Helmet>

      <div className="bg-ds-paper min-h-screen">
        <Header onSpecialistClick={openDemo} />
        <main className="flex flex-col gap-16 pb-16 md:gap-20">
          <LegalHero document={document} />
          <LegalHighlights highlights={document.highlights} />
          {document.audiencePaths && (
            <LegalAudiencePaths paths={document.audiencePaths} />
          )}

          <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8">
            <div className="lg:col-span-4 xl:col-span-3">
              <LegalToc sections={document.sections} activeId={activeId} />
            </div>
            <div className="flex min-w-0 flex-col gap-16 lg:col-span-8 xl:col-span-8">
              <div className="flex flex-col gap-10">
                {document.sections.map((section, index) => (
                  <LegalSectionView
                    key={section.id}
                    section={section}
                    number={index + 1}
                  />
                ))}
              </div>
              <LegalContactCard />
              <LegalChangelog changelog={document.changelog} />
              {!document.hideRelatedDocs && (
                <LegalRelatedDocs current={document.key} />
              )}
            </div>
          </div>
        </main>
        <Footer />
        <WhatsAppFloatButton />
        <CookieConsent />
        <DemoFormModal
          isOpen={isDemoOpen}
          onClose={() => setIsDemoOpen(false)}
        />
      </div>
    </>
  );
};
